/**
 * Filter Count Utilities - Dynamic Faceted Search
 *
 * Provides cross-filter counting for dynamic filter options.
 * When a filter is applied, counts for OTHER filter options are recalculated
 * based on the cells that match the current filter selection.
 *
 * Performance: Uses efficient single-pass counting with early exit for zero results.
 */

// ============================================================================
// GHOST / LOCKED CELL CLASSIFICATION
// ============================================================================

// Filter dimensions whose underlying field is HIDDEN on ghost/locked cards.
// When any of these is active, ghost cells must be excluded from counts,
// ranges and the grid (the user cannot reason about the match).
// Visible-on-ghost dimensions: manufacturers, formFactors, energy, power,
// capacity, current, search (name + manufacturer).
const HIDDEN_CATEGORICAL_KEYS = ['cathodeChemistries', 'anodeChemistries', 'technologies', 'countries']
const HIDDEN_RANGE_KEYS = ['year', 'batchVariation', 'aging']

/**
 * True iff at least one filter on a hidden-on-ghost field is active.
 * Inputs: the `currentFilterState` shape from filterStore.js.
 */
export function hasHiddenFilterActive(currentFilters) {
  if (!currentFilters) return false
  for (const key of HIDDEN_CATEGORICAL_KEYS) {
    const v = currentFilters[key]
    if (Array.isArray(v) && v.length > 0) return true
  }
  for (const key of HIDDEN_RANGE_KEYS) {
    if (currentFilters[key]?.active) return true
  }
  return false
}

// ============================================================================
// CROSS-FILTER COUNTING
// ============================================================================

/**
 * Apply a single filter to the cell list
 * @param {Array} cells - Array of cell objects
 * @param {string} filterType - Type of filter to apply
 * @param {*} filterValue - Value(s) for the filter
 * @param {string} currentBasis - Current basis (abs, grav, vol)
 * @returns {Array} Filtered cells
 */
function applyFilter(cells, filterType, filterValue, currentBasis) {
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
    return cells
  }

  switch (filterType) {
    case 'cathodeChemistries':
      return cells.filter(cell => {
        const cathodeGroup = cell.chemical?.cathode_material_group ||
                            cell.chemical?.main_chemistry ||
                            cell.chemical?.cathode
        return cathodeGroup && filterValue.includes(cathodeGroup)
      })

    case 'anodeChemistries':
      return cells.filter(cell => {
        const anodeGroup = cell.chemical?.anode_material_group || cell.chemical?.anode
        return anodeGroup && filterValue.includes(anodeGroup)
      })

    case 'manufacturers':
      return cells.filter(cell => cell.manufacturer && filterValue.includes(cell.manufacturer))

    case 'formFactors':
      return cells.filter(cell => {
        const cellFormat = cell.mechanical?.format_group || cell.mechanical?.format
        return cellFormat && filterValue.includes(cellFormat)
      })

    case 'technologies':
      return cells.filter(cell => {
        const cellTech = cell.technology || cell.chemical?.main_chemistry
        return cellTech && filterValue.includes(cellTech)
      })

    case 'countries':
      return cells.filter(cell => {
        const cellCountry = cell.general?.origin_country || cell.country
        return cellCountry && filterValue.includes(cellCountry)
      })

    case 'energy':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const cellEnergy = getCellValue(cell, 'energy', currentBasis)
        return cellEnergy !== null && cellEnergy >= filterValue.min && cellEnergy <= filterValue.max
      })

    case 'power':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const cellPower = getCellValue(cell, 'power', currentBasis)
        return cellPower !== null && cellPower >= filterValue.min && cellPower <= filterValue.max
      })

    case 'capacity':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const cellCapacity = getCellValue(cell, 'capacity', currentBasis)
        return cellCapacity !== null && cellCapacity >= filterValue.min && cellCapacity <= filterValue.max
      })

    case 'current':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const cellCurrent = getCellValue(cell, 'current', currentBasis)
        return cellCurrent !== null && cellCurrent >= filterValue.min && cellCurrent <= filterValue.max
      })

    case 'batchVariation':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const value = cell.batch?.CAP_spread_std_rel
        if (value === null || value === undefined) return false
        const pctValue = value * 100 // Convert fraction to percentage
        return pctValue >= filterValue.min && pctValue <= filterValue.max
      })

    case 'aging':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const value = cell.aging?.CAP_loss_rel
        if (value === null || value === undefined) return false
        const pctValue = value * 100 // Convert fraction to percentage
        return pctValue >= filterValue.min && pctValue <= filterValue.max
      })

    case 'year':
      if (!filterValue.active) return cells
      return cells.filter(cell => {
        const year = cell.year || cell.general?.year
        return year !== null && year >= filterValue.min && year <= filterValue.max
      })

    default:
      return cells
  }
}

/**
 * Get cell value for a specific metric and basis
 * @param {object} cell - Cell object
 * @param {string} metric - Metric type (energy, power, capacity, current)
 * @param {string} basisValue - Basis (abs, grav, vol)
 * @returns {number|null}
 */
function getCellValue(cell, metric, basisValue) {
  const electrical = cell.electrical
  if (!electrical) return null

  switch (metric) {
    case 'energy':
      if (basisValue === 'abs') return electrical.E_c10_Wh
      if (basisValue === 'grav') return electrical.E_grav_Wh_per_kg
      if (basisValue === 'vol') return electrical.E_vol_Wh_per_L
      return null

    case 'power':
      if (basisValue === 'abs') return electrical.P_peak_W
      if (basisValue === 'grav') return electrical.P_grav_W_per_kg
      if (basisValue === 'vol') return electrical.P_vol_W_per_L
      return null

    case 'capacity':
      if (basisValue === 'abs') return electrical.C_c10_Ah
      if (basisValue === 'grav') return electrical.C_grav_Ah_per_kg
      if (basisValue === 'vol') return electrical.C_vol_Ah_per_L
      return null

    case 'current':
      if (basisValue === 'abs') return electrical.I_peak_A
      if (basisValue === 'grav') return electrical.I_grav_A_per_kg
      if (basisValue === 'vol') return electrical.I_vol_A_per_L
      return null

    default:
      return null
  }
}

/**
 * Compute cross-filter counts for all filter options
 * For each filter category, applies ALL OTHER filters and counts remaining options
 *
 * @param {Array} allCells - All cells (unfiltered)
 * @param {object} currentFilters - Current filter selections
 * @param {string} currentBasis - Current basis (abs, grav, vol)
 * @returns {object} Counts per filter category
 */
export function computeCrossFilterCounts(allCells, currentFilters, currentBasis) {
  if (!allCells || allCells.length === 0) {
    return {
      cathodeChemistries: new Map(),
      anodeChemistries: new Map(),
      manufacturers: new Map(),
      formFactors: new Map(),
      technologies: new Map(),
      countries: new Map()
    }
  }

  const filterTypes = [
    'cathodeChemistries', 'anodeChemistries', 'manufacturers',
    'formFactors', 'technologies', 'countries',
    'energy', 'power', 'capacity', 'current',
    'batchVariation', 'aging', 'year'
  ]

  const counts = {}

  // For each categorical filter, compute counts excluding that filter
  const categoricalFilters = ['cathodeChemistries', 'anodeChemistries', 'manufacturers', 'formFactors', 'technologies', 'countries']

  for (const excludeFilter of categoricalFilters) {
    // Facet counts reflect the FULL catalogue, including locked (demo shadow) cells.
    // Locked cells still carry their categorical data (chemistry, technology, country)
    // in the response, so counting them shows the true "how many cells have this value"
    // market picture next to each filter option. This affects the displayed COUNTS only —
    // locked cells stay locked in the grid; the lock/unlock behaviour is unchanged.
    let filteredCells = [...allCells]

    for (const filterType of filterTypes) {
      if (filterType === excludeFilter) continue // Skip the filter we're counting

      const filterValue = currentFilters[filterType]
      filteredCells = applyFilter(filteredCells, filterType, filterValue, currentBasis)

      // Early exit if no cells left
      if (filteredCells.length === 0) break
    }

    // Count occurrences for this filter category
    counts[excludeFilter] = countByCategory(filteredCells, excludeFilter)
  }

  return counts
}

/**
 * Count cells by category field
 * @param {Array} cells - Cells to count
 * @param {string} category - Category to count by
 * @returns {Map<string, number>}
 */
function countByCategory(cells, category) {
  const counts = new Map()

  for (const cell of cells) {
    let value = null

    switch (category) {
      case 'cathodeChemistries':
        value = cell.chemical?.cathode_material_group ||
                cell.chemical?.main_chemistry ||
                cell.chemical?.cathode
        break

      case 'anodeChemistries':
        value = cell.chemical?.anode_material_group || cell.chemical?.anode
        break

      case 'manufacturers':
        value = cell.manufacturer
        break

      case 'formFactors':
        value = cell.mechanical?.format_group || cell.mechanical?.format
        break

      case 'technologies':
        value = cell.technology || cell.chemical?.main_chemistry
        break

      case 'countries':
        value = cell.general?.origin_country || cell.country
        break
    }

    if (value) {
      counts.set(value, (counts.get(value) || 0) + 1)
    }
  }

  return counts
}

// ============================================================================
// DYNAMIC RANGE COMPUTATION
// ============================================================================

/**
 * Compute dynamic min/max ranges for range sliders based on filtered cells
 *
 * @param {Array} filteredCells - Currently filtered cells
 * @param {string} currentBasis - Current basis (abs, grav, vol)
 * @returns {object} Dynamic ranges for each slider
 */
export function computeDynamicRanges(filteredCells, currentBasis) {
  if (!filteredCells || filteredCells.length === 0) {
    return {
      energy: { min: 0, max: 0, hasData: false },
      power: { min: 0, max: 0, hasData: false },
      capacity: { min: 0, max: 0, hasData: false },
      current: { min: 0, max: 0, hasData: false },
      batchVariation: { min: 0, max: 0, hasData: false },
      aging: { min: 0, max: 0, hasData: false },
      year: { min: 0, max: 0, hasData: false }
    }
  }

  const ranges = {
    energy: computeMinMax(filteredCells, 'energy', currentBasis),
    power: computeMinMax(filteredCells, 'power', currentBasis),
    capacity: computeMinMax(filteredCells, 'capacity', currentBasis),
    current: computeMinMax(filteredCells, 'current', currentBasis),
    batchVariation: computeMinMaxSimple(filteredCells, cell => {
      const val = cell.batch?.CAP_spread_std_rel
      return val !== null && val !== undefined ? val * 100 : null
    }),
    aging: computeMinMaxSimple(filteredCells, cell => {
      const val = cell.aging?.CAP_loss_rel
      return val !== null && val !== undefined ? val * 100 : null
    }),
    year: computeMinMaxSimple(filteredCells, cell => cell.year || cell.general?.year)
  }

  return ranges
}

/**
 * Compute min/max for a metric with basis awareness
 */
function computeMinMax(cells, metric, basisValue) {
  let min = Infinity
  let max = -Infinity
  let count = 0

  for (const cell of cells) {
    const value = getCellValue(cell, metric, basisValue)
    if (value !== null && value !== undefined && !isNaN(value)) {
      if (value < min) min = value
      if (value > max) max = value
      count++
    }
  }

  if (count === 0) {
    return { min: 0, max: 0, hasData: false }
  }

  // Round to nice values
  return {
    min: Math.floor(min),
    max: Math.ceil(max),
    hasData: true
  }
}

/**
 * Compute min/max using a value extractor function
 */
function computeMinMaxSimple(cells, getValue) {
  let min = Infinity
  let max = -Infinity
  let count = 0

  for (const cell of cells) {
    const value = getValue(cell)
    if (value !== null && value !== undefined && !isNaN(value)) {
      if (value < min) min = value
      if (value > max) max = value
      count++
    }
  }

  if (count === 0) {
    return { min: 0, max: 0, hasData: false }
  }

  return {
    min: Math.floor(min * 10) / 10, // Round to 1 decimal
    max: Math.ceil(max * 10) / 10,
    hasData: true
  }
}

// ============================================================================
// HISTOGRAM DISTRIBUTION
// ============================================================================

/**
 * Build histogram distribution for a range slider
 *
 * @param {Array} filteredCells - Currently filtered cells
 * @param {string} metric - Metric to build histogram for
 * @param {string} currentBasis - Current basis (abs, grav, vol)
 * @param {number} bins - Number of histogram bins (default 20)
 * @returns {Array} Distribution array with { min, max, count } per bin
 */
export function buildHistogramDistribution(filteredCells, metric, currentBasis, bins = 20) {
  if (!filteredCells || filteredCells.length === 0) {
    return []
  }

  // Get all values
  const values = []
  for (const cell of filteredCells) {
    let value
    if (metric === 'batchVariation') {
      const val = cell.batch?.CAP_spread_std_rel
      value = val !== null && val !== undefined ? val * 100 : null
    } else if (metric === 'aging') {
      const val = cell.aging?.CAP_loss_rel
      value = val !== null && val !== undefined ? val * 100 : null
    } else if (metric === 'year') {
      value = cell.year || cell.general?.year
    } else {
      value = getCellValue(cell, metric, currentBasis)
    }

    if (value !== null && value !== undefined && !isNaN(value)) {
      values.push(value)
    }
  }

  if (values.length === 0) {
    return []
  }

  // Compute range
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min

  if (range === 0) {
    // All values are the same
    return [{ min, max, count: values.length }]
  }

  // For year: use discrete bins (one per year) instead of fixed bin count
  if (metric === 'year') {
    const distribution = []
    for (let year = Math.floor(min); year <= Math.ceil(max); year++) {
      const count = values.filter(v => Math.floor(v) === year).length
      distribution.push({ min: year, max: year + 1, count })
    }
    return distribution
  }

  // Create bins for continuous metrics
  const binSize = range / bins
  const distribution = []

  for (let i = 0; i < bins; i++) {
    const binMin = min + i * binSize
    const binMax = min + (i + 1) * binSize
    const count = values.filter(v => v >= binMin && (i === bins - 1 ? v <= binMax : v < binMax)).length
    distribution.push({ min: binMin, max: binMax, count })
  }

  return distribution
}

// ============================================================================
// FILTER APPLICATION FOR RANGES (excluding one range)
// ============================================================================

/**
 * Compute cells filtered by all filters EXCEPT the specified range filter
 * Used to calculate dynamic range for a specific slider
 *
 * @param {Array} allCells - All cells
 * @param {object} currentFilters - Current filter state
 * @param {string} excludeRange - Range filter to exclude (e.g., 'energy')
 * @param {string} currentBasis - Current basis
 * @returns {Array} Filtered cells
 */
export function computeCellsExcludingRange(allCells, currentFilters, excludeRange, currentBasis) {
  if (!allCells || allCells.length === 0) {
    return []
  }

  const filterTypes = [
    'cathodeChemistries', 'anodeChemistries', 'manufacturers',
    'formFactors', 'technologies', 'countries',
    'energy', 'power', 'capacity', 'current',
    'batchVariation', 'aging', 'year'
  ]

  let filteredCells = [...allCells]

  for (const filterType of filterTypes) {
    if (filterType === excludeRange) continue // Skip the range we're calculating

    const filterValue = currentFilters[filterType]
    filteredCells = applyFilter(filteredCells, filterType, filterValue, currentBasis)

    // Early exit if no cells left
    if (filteredCells.length === 0) break
  }

  return filteredCells
}

/**
 * Compute all dynamic data for sliders at once
 * Returns ranges and histograms for each slider, excluding its own filter
 *
 * @param {Array} allCells - All cells
 * @param {object} currentFilters - Current filter state
 * @param {string} currentBasis - Current basis
 * @returns {object} { ranges: {...}, histograms: {...} }
 */
export function computeAllDynamicRangeData(allCells, currentFilters, currentBasis) {
  const rangeFilters = ['energy', 'power', 'capacity', 'current', 'batchVariation', 'aging', 'year']

  const ranges = {}
  const histograms = {}

  for (const rangeFilter of rangeFilters) {
    // Hidden-on-ghost range dims (year, batchVariation, aging) must never bound their
    // slider with values from locked cells — those fields are concealed on the card.
    const isHiddenRange = HIDDEN_RANGE_KEYS.includes(rangeFilter)
    const baseCells = isHiddenRange ? allCells.filter(c => !c.is_locked) : allCells

    // Get cells filtered by everything EXCEPT this range
    const cellsForRange = computeCellsExcludingRange(baseCells, currentFilters, rangeFilter, currentBasis)

    // Compute dynamic range
    if (rangeFilter === 'batchVariation') {
      ranges[rangeFilter] = computeMinMaxSimple(cellsForRange, cell => {
        const val = cell.batch?.CAP_spread_std_rel
        return val !== null && val !== undefined ? val * 100 : null
      })
    } else if (rangeFilter === 'aging') {
      ranges[rangeFilter] = computeMinMaxSimple(cellsForRange, cell => {
        const val = cell.aging?.CAP_loss_rel
        return val !== null && val !== undefined ? val * 100 : null
      })
    } else if (rangeFilter === 'year') {
      ranges[rangeFilter] = computeMinMaxSimple(cellsForRange, cell => cell.year || cell.general?.year)
    } else {
      ranges[rangeFilter] = computeMinMax(cellsForRange, rangeFilter, currentBasis)
    }

    // Build histogram
    histograms[rangeFilter] = buildHistogramDistribution(cellsForRange, rangeFilter, currentBasis)
  }

  return { ranges, histograms }
}
